import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Hero } from "./Webinar";
import { Images } from "../../../assets/images";
import { Helmet } from "react-helmet";
import { HyperLink } from "../../common/FormInputs";
import { PageLoader } from "../NewLandingPage/components/PageLoader";

const ModelClauseRules = () => {
  const [criticalImagesLoaded, setCriticalImagesLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const criticalImages = [Images.modalClause].filter(Boolean);

    if (criticalImages.length === 0) {
      setCriticalImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const totalImages = criticalImages.length;

    criticalImages.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setTimeout(() => setCriticalImagesLoaded(true), 300);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setTimeout(() => setCriticalImagesLoaded(true), 300);
        }
      };
      img.src = src;
    });

    // Fallback timeout - show content after 3 seconds regardless
    const timeout = setTimeout(() => {
      setCriticalImagesLoaded(true);
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  const clauses = [
    {
      title: "1. IAMC Model Dispute Resolution Clause",
      points: [
        "The parties shall resolve any dispute arising out of or in connection with anything contained in the Agreement or otherwise by negotiation, mediation or arbitration through IAMC [www.IAMC.co.in] within 30 (thirty days) of its occurrence or such extended time as may be mutually agreed by the parties.",
        <>
          If the parties refer the dispute for mediation, the Institution shall,
          among other things, appoint a neutral Mediator/Conciliator and provide
          a platform for settlement of dispute between the parties as per the{" "}
          <HyperLink
            onClick={() => window.open(`https://user.iamch.org.in/mediation-rules`)}
          >
            Mediation Rules
          </HyperLink>{" "}
          of the Institution. The mediation may be conducted online.
        </>,
        <>
          If the parties refer the dispute for arbitration, the Institution
          shall, among other things, appoint neutrals (Mediators) and/or
          Arbitrator(s) and the arbitral proceeding may be conducted as per{" "}
          <HyperLink
            onClick={() =>
              window.open(`https://user.iamch.org.in/arbitration-rules`)
            }
          >
            Arbitration Rules
          </HyperLink>{" "}
          of the Institution or rules agreed between the parties.
        </>,
        "The arbitration shall be in accordance with the Arbitration and Conciliation Act, 1996 or any statutory modification or re-enactment thereof. The seat and venue of the arbitration shall be Chennai, India and language of the proceeding shall be English. The award of the Arbitrator shall be final and binding on the Parties. The arbitration may be conducted online.",
        "Should this clause create a contradiction with a statutory requirement or a judicial mandate to resolve certain types of disputes through specific courts, the statutory requirement shall prevail.",
      ],
    },
    {
      title:
        "2. IAMC Model Dispute Resolution Clause (Sequential Escalation)",
      points: [
        "The parties shall resolve any dispute arising out of or in connection with anything contained in the Agreement or otherwise by negotiation through IAMC [www.IAMC.co.in] within 30 (thirty days) of its occurrence or such extended time as may be mutually agreed by the parties. The parties may choose any other ADR institution provided such institution is as agreed mutually by the parties.",
        "If the dispute has not been resolved through negotiation as provided herein, the parties shall endeavour to refer the dispute/disputes for mediation through IAMC [www.IAMC.co.in]. The Institution shall, among other things, appoint a neutral Mediator/Conciliator and endeavour for settlement of dispute between the parties as per the mediation Rules of IAMC. The mediation may be conducted online.",
        "If the dispute has not been resolved either through negotiation or mediation, as provided the parties shall refer the dispute for arbitration by appointing Arbitrator/ Arbitrators through IAMC [www.IAMC.co.in] as per IAMC Arbitration Rules (JAR) or rules agreeable to parties. The arbitration shall be in accordance with the Arbitration and Conciliation Act, 1996 or any statutory modification or re-enactment thereof. The seat of arbitration shall be Chennai, India and language of the proceeding shall be English. The award of the Arbitrator shall be final and binding on the Parties. The arbitration may be conducted online.",
        "The Institution shall, among other things, appoint neutrals (Mediators) and/or Arbitrators) and provide a platform for settlement of dispute between the parties as per the mediation Rules of the Institution.",
        "Should this clause create a contradiction with a statutory requirement or a judicial mandate to resolve certain types of disputes through specific courts, the statutory requirement shall prevail.",
      ],
    },
    {
      title: "3. IAMC Model Negotiation Clause",
      points: [
        <>
          The parties shall endeavour to resolve any dispute arising out of or
          in connection with anything contained in the Agreement or otherwise by{" "}
          <HyperLink
            onClick={() =>
              window.open(`https://user.iamch.org.in/negotiation-model-clause`)
            }
          >
            negotiation
          </HyperLink>{" "}
          through IAMC [www.justact.co.in] within 30 (thirty days) of its
          occurrence or such extended time as may be mutually agreed by the
          parties.
        </>,
        "The Institution shall, among other things, appoint a neutral Mediator/Conciliator and provide a platform for settlement of dispute between the parties as per the negotiation Rules of the Institution. The settlement agreement resulting from the negotiation shall be provided in the form of a Conciliation Award as per section 74 of the Arbitration and Conciliation Act, 1996.",
        "Should this clause create a contradiction with a statutory requirement or a judicial mandate to resolve certain types of disputes through specific courts, the statutory requirement shall prevail.",
      ],
    },
    {



      title: "4. IAMC Model Mediation Clause",
      points: [
        <>
          In the event of any dispute arising out of or in connection with
          anything contained in the Agreement or otherwise, such dispute shall
          be referred to by Parties, for{" "}
          <HyperLink
            onClick={() =>
              window.open(`https://user.iamch.org.in/mediation-model-clause`)
            }
          >
            mediation
          </HyperLink>{" "}
          through IAMC [www.IAMC.co.in] ("Institution").
        </>,
        "The parties may use any other ADR institution provided such Institution is as agreed mutually by the parties.",
        "The Institution shall, among other things, appoint a neutral Mediator/Conciliator and provide a platform for settlement of dispute between the parties as per the mediation Rules of the Institution.",
        "Should this clause create a contradiction with a statutory requirement or a judicial mandate to resolve certain types of disputes through specific courts, the statutory requirement shall prevail.",
      ],
    },
    {
      title: "5. IAMC Model Arbitration Clause",
      points: [
        <>
          In the event of any dispute arising out of or in connection with
          anything contained in the Agreement [or otherwise], the parties shall
          refer the dispute for{" "}
          <HyperLink
            onClick={() =>
              window.open(`https://user.iamch.org.in/arbitration-model-clause`)
            }
          >
            {" "}
            arbitration
          </HyperLink>{" "}
          through IAMC [www.justact.co.in] ("Institution").
        </>,
        "The parties may use any other ADR institution provided such Institution is agreed mutually by the parties.",
        "The arbitration shall be in accordance with the Arbitration and Conciliation Act, 1996 or any statutory modification or re-enactment thereof. The seat of arbitration shall be Chennai, India and language of the proceeding shall be English. The award of the Arbitrator shall be final and binding on the Parties. The arbitration may be conducted online.",
        "The Institution shall, among other things, appoint neutral Arbitrator(s) and provide a platform for resolution of the dispute between the parties as per the arbitration Rules of the Institution.",
        "Should this clause create a contradiction with a statutory requirement or a judicial mandate to resolve certain types of disputes through specific courts, the statutory requirement shall prevail.",
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <title>
          Model Clauses - Dispute Resolution, Negotiation, Mediation &
          Arbitration Clauses
        </title>
        <meta
          name="description"
          content="IAMC Model Clause has been designed to support the resolution of disputes in a time and cost-effective manner."
        />
        <meta
          name="keywords"
          content="Model Dispute Resolution Clause, Dispute Resolution Online, Dispute Resolution Clause, Negotiation Clause, Mediation Clause, Arbitration Clauses, Alternative Dispute Resolution Clauses, Resolve Disputes Online, IAMC"
        />
      </Helmet>
      <PageLoader isLoading={!criticalImagesLoaded} />
      <PageContainer>
        <Hero bgImage={Images.modalClause}>
          <HeroTitle>Model Clauses</HeroTitle>
        </Hero>

        <ContentSection>
          {clauses.map((clause, index) => (
            <ClauseCard key={index}>
              <ClauseTitle>{clause.title}</ClauseTitle>
              <ClauseList>
                {clause.points.map((point, pointIndex) => (
                  <ClausePoint key={pointIndex}>{point}</ClausePoint>
                ))}
              </ClauseList>
            </ClauseCard>
          ))}
        </ContentSection>
      </PageContainer>
    </>
  );
};

export default ModelClauseRules;

export const PageContainer = styled.div`
  color: #293461;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
`;

export const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 600;
  color: white;
  text-align: center;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const ContentSection = styled.section`
  width: 100%;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

export const ClauseCard = styled.div`
  background: white;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const ClauseTitle = styled.h2`
  color: #00838c;
  font-size: 22px;
  margin-bottom: 1.2rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const ClauseList = styled.ul`
  padding-left: 1.5rem;
  margin: 0;
`;

export const ClausePoint = styled.li`
  margin-bottom: 1rem;
  line-height: 1.8;
  color: #293461;
  font-size: 1rem;
  font-family: openSans-Regular, sans-serif;
  text-align: justify;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;
